import React, { useEffect } from 'react'
import { Eye, Package, Truck, CheckCircle, XCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useNavigate } from 'react-router-dom'
import apiClient from '@/lib/apiClient'
import { useQuery } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { ApiResponse } from '@/types/api'
import { formatIDR } from '@/lib/utils'

interface RecentOrder {
  id: string
  order_number: string
  customer: {
    name: string
    email: string
  }
  total_amount: number
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded'
  payment_status: string
  created_at: string
  status_display: string
}

interface RecentOrdersProps {
  className?: string
}

const getStatusIcon = (status: RecentOrder['status']) => {
  switch (status) {
    case 'pending':
      return <Package className="h-4 w-4" />
    case 'processing':
      return <Package className="h-4 w-4" />
    case 'shipped':
      return <Truck className="h-4 w-4" />
    case 'delivered':
      return <CheckCircle className="h-4 w-4" />
    case 'cancelled':
      return <XCircle className="h-4 w-4" />
    case 'refunded':
      return <XCircle className="h-4 w-4" />
    default:
      return <Package className="h-4 w-4" />
  }
}

const getStatusColor = (status: RecentOrder['status']) => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800'
    case 'processing':
      return 'bg-blue-100 text-blue-800'
    case 'shipped':
      return 'bg-purple-100 text-purple-800'
    case 'delivered':
      return 'bg-green-100 text-green-800'
    case 'cancelled':
      return 'bg-red-100 text-red-800'
    case 'refunded':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getPaymentLabel = (status: string) =>
  status
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')

const OrderSkeleton = () => (
  <div className="flex items-center justify-between rounded-2xl border border-slate-200 p-4">
    <div className="flex items-center space-x-3">
      <Skeleton className="h-8 w-8 rounded-full" />
      <div>
        <Skeleton className="h-4 w-24 mb-1" />
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
    <div className="text-right">
      <Skeleton className="h-4 w-16 mb-1 ml-auto" />
      <Skeleton className="h-3 w-20 ml-auto" />
    </div>
    <Skeleton className="h-8 w-8" />
  </div>
)

// Add formatDate function
const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  } catch {
    return dateString
  }
}

export const RecentOrders: React.FC<RecentOrdersProps> = ({ className }) => {
  const navigate = useNavigate()
  const { toast } = useToast()

  const {
    data: response,
    isLoading,
    error,
  } = useQuery<ApiResponse<RecentOrder[]>>({
    queryKey: ['recentOrders'],
    queryFn: () => apiClient.getRecentOrders({ limit: 5 }),
  })

  const orders = response?.data || []

  const handleViewOrder = (orderId: string) => {
    navigate(`/orders/${orderId}`)
  }

  const handleViewAllOrders = () => {
    navigate('/orders')
  }

  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to load recent orders',
        variant: 'destructive',
      })
    }
  }, [error, toast])

  return (
    <Card className={cn('rounded-3xl border-slate-200/80 bg-white shadow-sm', className)}>
      <CardHeader className="border-b border-slate-100 pb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-lg font-semibold text-slate-950">Recent Orders</CardTitle>
            <CardDescription className="mt-1 text-sm text-slate-500">
              Latest customer orders and current fulfillment state.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={handleViewAllOrders}
          >
            View All
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-4">
          {isLoading ? (
            <>
              <OrderSkeleton />
              <OrderSkeleton />
              <OrderSkeleton />
            </>
          ) : orders.length > 0 ? (
            orders.map((order: RecentOrder) => (
              <div
                key={order.id}
                className="group flex items-center gap-4 rounded-2xl border border-slate-200/80 bg-white p-4 transition-colors hover:bg-slate-50"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className={cn('rounded-2xl p-2.5', getStatusColor(order.status))}>
                    {getStatusIcon(order.status)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {order.order_number}
                      </p>
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold',
                          getStatusColor(order.status)
                        )}
                      >
                        {order.status_display}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-sm text-slate-600">{order.customer.name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span>{order.customer.email}</span>
                      <span>Payment: {getPaymentLabel(order.payment_status)}</span>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-900">
                    {formatIDR(order.total_amount)}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{formatDate(order.created_at)}</p>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="rounded-full"
                  onClick={() => handleViewOrder(order.id)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 py-10 text-center text-slate-500">
              No recent orders found
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
